import type { MouseEventHandler, ReactNode, Ref } from 'react';

import { Box } from '@mui/material';

interface DrawerHeaderButtonProps {
  children: ReactNode;
  /** Accessible name for the icon-only button. */
  label: string;
  onClick?: MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  /** When defined, renders a bottom indicator bar (highlighted when `true`). */
  active?: boolean;
  ref?: Ref<HTMLElement>;
}

/**
 * The 48×108 icon button used in a drawer's chrome header (back / close /
 * history). A plain box — deliberately not an MUI ButtonBase — so it has no
 * ripple and a fixed full-header height, consistent across every drawer.
 */
export const DrawerHeaderButton = ({
  children,
  label,
  onClick,
  disabled = false,
  active,
  ref
}: DrawerHeaderButtonProps) => (
  <Box
    ref={ref as Ref<HTMLDivElement>}
    aria-label={label}
    role='button'
    tabIndex={disabled ? -1 : 0}
    sx={{
      width: 48,
      height: 108,
      flexShrink: 0,
      backgroundColor: 'gx.tileDark',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
      ...(active !== undefined && {
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: active ? 'gx.highlight' : 'transparent'
        }
      })
    }}
    onClick={disabled ? undefined : onClick}
  >
    {children}
  </Box>
);
